"use client";

import { forwardRef, useState } from "react";
import { format } from "date-fns";
import {
	DayPicker,
	type DateRange as DayPickerDateRange,
} from "react-day-picker";
import * as Popover from "@radix-ui/react-popover";
import { cn } from "~/lib/utils";
import { CalendarIcon, ChevronDownIcon } from "~/components/icons";

import "react-day-picker/style.css";

export interface DateRange {
	from: Date | null;
	to: Date | null;
}

export interface DateRangePickerProps extends Omit<
	React.HTMLAttributes<HTMLDivElement>,
	"onChange"
> {
	value?: DateRange;
	onChange?: (range: DateRange) => void;
	placeholder?: string;
	disabled?: boolean;
	size?: "sm" | "md" | "lg";
}

const sizeStyles = {
	sm: "h-8 text-sm pl-9 pr-3",
	md: "h-10 text-sm pl-10 pr-4",
	lg: "h-[60px] text-base pl-12 pr-4",
};

const iconSizeStyles = {
	sm: "left-3 h-4 w-4",
	md: "left-3 h-4 w-4",
	lg: "left-4 h-5 w-5",
};

const DateRangePicker = forwardRef<HTMLDivElement, DateRangePickerProps>(
	(
		{
			className,
			value,
			onChange,
			placeholder = "Select date range",
			disabled = false,
			size = "md",
			...props
		},
		ref,
	) => {
		const [open, setOpen] = useState(false);

		const formatDateDisplay = (date: Date | null) => {
			if (!date) return "";
			return format(date, "MMM d, yyyy");
		};

		const displayValue =
			value?.from && value?.to
				? `${formatDateDisplay(value.from)} - ${formatDateDisplay(value.to)}`
				: value?.from
					? `${formatDateDisplay(value.from)} - Select end date`
					: placeholder;

		const handleSelect = (range: DayPickerDateRange | undefined) => {
			onChange?.({
				from: range?.from ?? null,
				to: range?.to ?? null,
			});
		};

		const selectedRange: DayPickerDateRange | undefined = value?.from
			? {
				from: value.from,
				to: value.to ?? undefined,
			}
			: undefined;

		return (
			<div ref={ref} className={cn("relative", className)} {...props}>
				<Popover.Root open={open} onOpenChange={setOpen}>
					<Popover.Trigger asChild>
						<button
							type="button"
							disabled={disabled}
							className={cn(
								"flex w-full items-center justify-between rounded-md border border-[#d9d9e0] bg-white text-left transition-colors hover:border-[#8b8d98] focus:border-[#1c3144] focus:outline-none focus:ring-2 focus:ring-[rgba(0,85,199,0.1)] disabled:cursor-not-allowed disabled:opacity-50",
								sizeStyles[size],
								!value?.from && "text-[#60646c]",
								value?.from && "text-[#1c2024]",
							)}
						>
							<span className="truncate">{displayValue}</span>
							<ChevronDownIcon className="h-4 w-4 shrink-0 text-[#60646c]" />
						</button>
					</Popover.Trigger>
					<div
						className={cn(
							"pointer-events-none absolute top-1/2 -translate-y-1/2",
							iconSizeStyles[size],
						)}
					>
						<CalendarIcon className="h-full w-full text-[#60646c]" />
					</div>

					<Popover.Portal>
						<Popover.Content
							className="z-50 rounded-lg border border-[#e8e8ec] bg-white p-4 shadow-lg animate-in fade-in-0 zoom-in-95"
							sideOffset={4}
							align="start"
						>
							<DayPicker
								mode="range"
								selected={selectedRange}
								onSelect={handleSelect}
								numberOfMonths={2}
								showOutsideDays
								classNames={{
									root: "rdp-custom",
									months: "flex gap-4",
									month: "space-y-4",
									month_caption: "flex justify-center items-center h-8",
									caption_label: "text-sm font-medium text-[#1c2024]",
									nav: "flex items-center gap-1",
									button_previous:
										"absolute left-1 top-0 h-8 w-8 flex items-center justify-center rounded hover:bg-[#f0f0f3] text-[#60646c]",
									button_next:
										"absolute right-1 top-0 h-8 w-8 flex items-center justify-center rounded hover:bg-[#f0f0f3] text-[#60646c]",
									month_grid: "w-full border-collapse",
									weekdays: "flex",
									weekday: "text-[#60646c] rounded-md w-9 font-normal text-xs",
									week: "flex w-full mt-2",
									day: "h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20 [&.rdp-selected]:bg-[#F6F9FD] [&.rdp-selected]:text-[#1C3144] [&.rdp-selected]:opacity-100",
									day_button:
										"h-9 w-9 p-0 font-normal rounded-md hover:bg-[#f0f0f3] focus:outline-none focus:ring-2 focus:ring-[rgba(0,85,199,0.2)] [&.rdp-selected]:hover:bg-[#F6F9FD]",
									selected: "!bg-[#1C3144] !text-[#1C3144] !opacity-100",
									today: "ring-1 ring-inset ring-[#1c3144] [&>button]:hover:!bg-transparent",
									outside: "text-[#8b8d98]",
									disabled: "text-[#8b8d98] opacity-50 pointer-events-none",
									range_middle:
										"!bg-[#F6F9FD] !text-[#1C3144] rounded-none !opacity-100",
									range_start:
										"rounded-l-md !bg-[#1C3144] !text-[#F6F9FD] !opacity-100 [&>button]:hover:!bg-transparent",
									range_end:
										"rounded-r-md !bg-[#1C3144] !text-[#F6F9FD] !opacity-100 [&>button]:hover:!bg-transparent",
									hidden: "invisible",
								}}
							/>
							<div className="mt-4 flex justify-end gap-2 border-t border-[#e8e8ec] pt-4">
								<button
									type="button"
									onClick={() => {
										onChange?.({ from: null, to: null });
										setOpen(false);
									}}
									className="h-8 rounded-md border border-[rgba(0,78,154,0.47)] px-3 text-sm font-medium text-[rgba(0,50,97,0.7)] hover:bg-[rgba(0,85,199,0.04)]"
								>
									Clear
								</button>
								<button
									type="button"
									onClick={() => setOpen(false)}
									className="h-8 rounded-md bg-[#1c3144] px-3 text-sm font-medium text-white hover:bg-[#2a4558]"
								>
									Apply
								</button>
							</div>
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			</div>
		);
	},
);

DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker };
